import { useState } from 'react';
import { ChevronDown, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// Scope picker for special days and emergencies over the WhatsApp gateway catalog (payload
// `gateways`): value null = "all numbers" (which also covers the in-app chat channel); an
// explicit array scopes to those numbers only — never the in-app chat — and must stay
// non-empty (the dialogs refuse to save an empty selection — the server rejects it too).
// Unticking "All numbers" seeds the full catalog so the state never starts out invalid.
// A scoped botId missing from the catalog (a renamed or retired gateway) renders as an
// extra "unknown number" row so an existing scope never becomes uneditable.

export function NumberMultiSelect({ gateways = [], value = null, disabled = false, onChange }) {
  const [open, setOpen] = useState(false);
  const all = value == null;
  const catalogIds = gateways.map((gateway) => gateway.botId);
  const selected = all ? catalogIds : value;
  const known = new Set(catalogIds);
  const unknown = (all ? [] : value).filter((botId) => !known.has(botId));
  const one =
    !all && selected.length === 1 ? gateways.find((g) => g.botId === selected[0]) : null;
  const label = all
    ? 'All numbers'
    : selected.length === 0
      ? 'No numbers'
      : selected.length === 1
        ? one?.label || selected[0]
        : `${selected.length} numbers`;

  const toggleNumber = (botId) => {
    const base = all ? [...catalogIds] : [...selected];
    onChange(
      base.includes(botId) ? base.filter((existing) => existing !== botId) : [...base, botId],
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className="h-8 gap-2 text-sm font-normal"
        >
          <Smartphone className="size-3.5 opacity-60" aria-hidden />
          {label}
          <ChevronDown className="size-3.5 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-80 p-1.5">
        <label className="flex cursor-pointer items-center gap-2.5 rounded-md border-b border-border px-2 py-2 transition hover:bg-muted">
          <input
            type="checkbox"
            checked={all}
            onChange={(event) => onChange(event.target.checked ? null : [...catalogIds])}
            aria-label="All numbers"
            className="size-3.5 shrink-0 rounded border-border accent-primary"
          />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-foreground">All numbers</span>
            <span className="block text-[11px] text-muted-foreground">
              Every WhatsApp number and the in-app chat, including numbers added later. An
              explicit selection covers only those numbers.
            </span>
          </span>
        </label>
        <div className="mt-1 max-h-56 space-y-0.5 overflow-y-auto">
          {gateways.length === 0 && unknown.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              No WhatsApp numbers are configured on this server.
            </p>
          ) : null}
          {gateways.map((gateway) => (
            <label
              key={gateway.botId}
              className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 transition hover:bg-muted"
            >
              <input
                type="checkbox"
                checked={selected.includes(gateway.botId)}
                onChange={() => toggleNumber(gateway.botId)}
                aria-label={`Include ${gateway.label}`}
                className="size-3.5 shrink-0 rounded border-border accent-primary"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground">
                  {gateway.label}
                </span>
                <span className="block truncate font-mono text-[11px] text-muted-foreground">
                  {gateway.botId}
                </span>
              </span>
            </label>
          ))}
          {unknown.map((botId) => (
            <label
              key={botId}
              title="This number is in the scope but missing from the gateway catalog — it may have been renamed or retired."
              className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 transition hover:bg-muted"
            >
              <input
                type="checkbox"
                checked
                onChange={() => toggleNumber(botId)}
                aria-label={`Include ${botId}`}
                className="size-3.5 shrink-0 rounded border-border accent-primary"
              />
              <span className="min-w-0 flex-1 truncate font-mono text-sm font-medium text-foreground">
                {botId}
              </span>
              <span className="shrink-0 rounded-full border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                unknown number
              </span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
